import { useState, useEffect, useCallback, useMemo } from "react";
import { toast } from "sonner";
import {
  TrendingUp, Users, DollarSign, Percent, Sparkles, Loader2, Bookmark, BookmarkCheck,
  Trash2, Copy, Download, Lightbulb, AlertTriangle, Target,
} from "lucide-react";
import { AIResourcesShell, StepLabel } from "../../components/ai/AIResourcesShell";
import { ResultPaywall } from "../../components/ai/ResultPaywall";
import { aiMarketInsights, aiSave, aiListSaved, aiDeleteSaved } from "../../lib/api";
import { useAuth } from "../../hooks/useAuth";
import { useAIResources } from "../../hooks/useAIResources";
import { copyToClipboard } from "../../lib/clipboard";

const money = (n) => {
  const v = Number(n) || 0;
  if (v >= 1_000_000_000) return `$${(v / 1_000_000_000).toFixed(1)}B`;
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `$${(v / 1_000).toFixed(1)}K`;
  return `$${Math.round(v).toLocaleString()}`;
};
const num = (n) => Math.round(Number(n) || 0).toLocaleString();

const verdictColor = (v) =>
  ({ Small: "#ef4444", Moderate: "#D4AF37", Large: "#22c55e", Massive: "#16a34a" }[v] || "#D4AF37");

export default function MarketSizeCalculator() {
  const { user, requireAuth } = useAuth();
  const { market, patchMarket } = useAIResources();
  const { idea, inputs, insights } = market;

  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState([]);
  const [savedFlag, setSavedFlag] = useState(false);

  const loadSaved = useCallback(() => {
    if (!user) { setSaved([]); return; }
    aiListSaved("market").then(setSaved).catch(() => setSaved([]));
  }, [user]);
  useEffect(() => { loadSaved(); }, [loadSaved]);

  const setInput = (key, value) => {
    patchMarket({ inputs: { ...inputs, [key]: value } });
    setSavedFlag(false);
  };

  // ---- Core calculations (pure JS) ----
  const calc = useMemo(() => {
    const totalMarket = Math.max(0, Number(inputs.totalMarket) || 0);
    const penetration = Math.max(0, Math.min(100, Number(inputs.penetration) || 0));
    const price = Math.max(0, Number(inputs.price) || 0);
    const conversion = Math.max(0, Math.min(100, Number(inputs.conversion) || 0));
    const growth = Math.max(0, Number(inputs.growth) || 0);

    const reachable = totalMarket * (penetration / 100); // SAM (people)
    const payingCustomers = reachable * (conversion / 100); // SOM (people)
    const monthlyRevenue = payingCustomers * price;
    const annualRevenue = monthlyRevenue * 12;

    const tamValue = totalMarket * price * 12;
    const samValue = reachable * price * 12;
    const somValue = annualRevenue;

    const projection = Array.from({ length: 5 }, (_, i) => ({
      year: i + 1,
      revenue: annualRevenue * Math.pow(1 + growth / 100, i),
    }));

    return { totalMarket, penetration, price, conversion, growth, reachable, payingCustomers, monthlyRevenue, annualRevenue, tamValue, samValue, somValue, projection };
  }, [inputs]);

  const runInsights = async () => {
    if (!idea.trim()) { toast.error("Describe your business idea first"); return; }
    setLoading(true);
    try {
      const r = await aiMarketInsights({ idea: idea.trim() });
      const est = r.estimates || {};
      patchMarket({
        inputs: {
          ...inputs,
          totalMarket: est.total_market ?? inputs.totalMarket,
          penetration: est.penetration ?? inputs.penetration,
          price: est.price ?? inputs.price,
          conversion: est.conversion ?? inputs.conversion,
          growth: est.growth ?? inputs.growth,
        },
        insights: r,
      });
      setSavedFlag(false);
      toast.success("Market estimated — numbers updated below");
      setTimeout(() => document.getElementById("mkt-results")?.scrollIntoView({ behavior: "smooth", block: "start" }), 250);
    } catch { toast.error("Could not estimate the market. Try again."); }
    finally { setLoading(false); }
  };

  const snapshot = () => ({
    idea: idea.trim() || "Market Size Estimate",
    inputs: { ...inputs },
    total_market: Math.round(calc.totalMarket),
    reachable_customers: Math.round(calc.reachable),
    paying_customers: Math.round(calc.payingCustomers),
    monthly_revenue: Math.round(calc.monthlyRevenue),
    annual_revenue: Math.round(calc.annualRevenue),
    tam_value: Math.round(calc.tamValue),
    sam_value: Math.round(calc.samValue),
    som_value: Math.round(calc.somValue),
    projection: calc.projection.map((p) => ({ year: p.year, revenue: Math.round(p.revenue) })),
    insights: insights || null,
  });

  const saveCalc = () => {
    if (savedFlag) { toast.info("Already saved — see your Startup Notebook"); return; }
    const doSave = async () => {
      try { await aiSave("market", snapshot()); setSavedFlag(true); toast.success("Calculation saved to your Startup Notebook"); loadSaved(); }
      catch (e) { console.error("save market failed", e); toast.error("Could not save"); }
    };
    if (user) doSave(); else requireAuth(doSave);
  };

  const copySummary = async () => {
    const t = [
      `Market Size — ${idea.trim() || "My Startup"}`,
      `Total addressable audience: ${num(calc.totalMarket)}`,
      `Reachable (SAM): ${num(calc.reachable)} · Paying customers (SOM): ${num(calc.payingCustomers)}`,
      `Monthly revenue: ${money(calc.monthlyRevenue)} · Annual revenue: ${money(calc.annualRevenue)}`,
      `TAM ${money(calc.tamValue)} · SAM ${money(calc.samValue)} · SOM ${money(calc.somValue)}`,
      `5-yr projection: ${calc.projection.map((p) => money(p.revenue)).join(" → ")}`,
    ].join("\n");
    (await copyToClipboard(t)) ? toast.success("Summary copied") : toast.error("Copy failed");
  };

  const openSaved = (row) => {
    const d = row.data || {};
    patchMarket({ idea: d.idea || "", inputs: { ...inputs, ...(d.inputs || {}) }, insights: d.insights || null });
    setSavedFlag(true);
  };
  const delSaved = async (id) => { setSaved((p) => p.filter((x) => x.id !== id)); try { await aiDeleteSaved(id); } catch { loadSaved(); } };

  const sidebar = (
    <div className="rounded-2xl border border-gray-100 bg-white p-5">
      <p className="font-display font-bold text-[#0F0F0F]">Saved Estimates</p>
      <p className="text-xs text-[#687280] mt-1">{saved.length} saved.</p>
      <div className="mt-3 space-y-2.5">
        {saved.length === 0 ? <p className="text-xs text-[#9aa1ab]">Save a calculation to revisit it.</p> : saved.slice(0, 6).map((s) => (
          <div key={s.id} className="flex items-center justify-between gap-2" data-testid={`mkt-saved-${s.id}`}>
            <button onClick={() => openSaved(s)} className="flex items-center gap-2 min-w-0 text-left">
              <BookmarkCheck size={14} className="text-[#D4AF37] shrink-0" />
              <span className="text-sm text-[#0F0F0F] truncate">{s.data?.idea}</span>
            </button>
            <div className="flex items-center gap-1.5 shrink-0">
              <span className="text-xs font-bold text-[#8a6d1a]">{money(s.data?.annual_revenue)}</span>
              <button onClick={() => delSaved(s.id)} className="text-[#9aa1ab] hover:text-red-500" data-testid={`mkt-saved-del-${s.id}`}><Trash2 size={13} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <AIResourcesShell active="market-size" title="Market Size Calculator" subtitle="Estimate your market opportunity — TAM, SAM, SOM, revenue potential and a five-year projection." icon={TrendingUp} extra={sidebar}>
      {/* Idea */}
      <section className="rounded-3xl border border-gray-100 bg-white p-6 shadow-[0_4px_20px_rgb(0,0,0,0.03)]" data-testid="mkt-idea">
        <StepLabel n={1} />
        <h2 className="font-display text-xl font-bold text-[#0F0F0F] mt-1">What are you building?</h2>
        <p className="text-sm text-[#687280] mt-1">Describe your idea and let AI estimate the real market numbers for you.</p>
        <div className="flex flex-col sm:flex-row gap-3 mt-4">
          <input
            value={idea}
            onChange={(e) => { patchMarket({ idea: e.target.value }); setSavedFlag(false); }}
            onKeyDown={(e) => e.key === "Enter" && runInsights()}
            placeholder="e.g. AI study platform for college students"
            className="flex-1 rounded-full border border-gray-200 px-5 py-3.5 text-sm focus:outline-none focus:border-[#D4AF37]"
            data-testid="mkt-idea-input"
          />
          <button onClick={runInsights} disabled={loading} className="inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-[#0F0F0F] rounded-full px-6 py-3.5 text-sm font-bold hover:bg-[#c5a030] transition-colors disabled:opacity-60 shrink-0" data-testid="mkt-estimate-btn">
            {loading ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />} {insights ? "Re-estimate Market" : "Estimate My Market"}
          </button>
        </div>
      </section>

      {/* Inputs */}
      <section className="rounded-3xl border border-gray-100 bg-white p-6 shadow-[0_4px_20px_rgb(0,0,0,0.03)] mt-6" data-testid="mkt-inputs">
        <StepLabel n={2} />
        <div className="flex items-center justify-between gap-3 mt-1">
          <h2 className="font-display text-xl font-bold text-[#0F0F0F]">Your market model</h2>
          {loading ? (
            <span className="inline-flex items-center gap-1.5 text-[11px] font-bold text-[#8a6d1a] bg-[#FBF7EA] border border-[#D4AF37]/30 rounded-full px-3 py-1"><Loader2 size={12} className="animate-spin" /> Estimating with AI…</span>
          ) : insights ? (
            <span className="text-[11px] font-bold text-[#8a6d1a] bg-[#FBF7EA] border border-[#D4AF37]/30 rounded-full px-3 py-1">AI-estimated · drag to adjust</span>
          ) : null}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6 mt-5">
          <Field icon={Users} label="Total potential audience" hint="How many people/businesses could ever use this" value={inputs.totalMarket} suffix="people" min={0} max={100_000_000} step={10000} onChange={(v) => setInput("totalMarket", v)} testid="totalMarket" fmt={num} />
          <Field icon={Percent} label="Market penetration" hint="Realistic share you can reach" value={inputs.penetration} suffix="%" min={0} max={100} step={0.5} onChange={(v) => setInput("penetration", v)} testid="penetration" />
          <Field icon={DollarSign} label="Price per customer" hint="Average monthly revenue per customer" value={inputs.price} prefix="$" suffix="/mo" min={0} max={2000} step={1} onChange={(v) => setInput("price", v)} testid="price" />
          <Field icon={Target} label="Conversion rate" hint="Of those reached, % who pay" value={inputs.conversion} suffix="%" min={0} max={100} step={0.5} onChange={(v) => setInput("conversion", v)} testid="conversion" />
          <Field icon={TrendingUp} label="Annual growth" hint="Expected yearly revenue growth" value={inputs.growth} suffix="%" min={0} max={300} step={1} onChange={(v) => setInput("growth", v)} testid="growth" />
        </div>
      </section>

      {/* Results */}
      <ResultPaywall label="market research" testid="mkt-paywall">
      <section className="mt-6 space-y-6" data-testid="mkt-results">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="font-display text-2xl font-extrabold text-[#0F0F0F]">Your Market Opportunity</h2>
          <div className="flex items-center gap-2">
            <button onClick={copySummary} className="inline-flex items-center gap-1.5 border border-gray-200 rounded-full px-3.5 py-2 text-xs font-semibold text-[#374151] hover:border-[#D4AF37]" data-testid="mkt-copy"><Copy size={13} /> Copy Summary</button>
            <button onClick={() => window.print()} className="inline-flex items-center gap-1.5 border border-gray-200 rounded-full px-3.5 py-2 text-xs font-semibold text-[#374151] hover:border-[#D4AF37]" data-testid="mkt-pdf"><Download size={13} /> Download PDF</button>
            <button onClick={saveCalc} className="inline-flex items-center gap-1.5 bg-[#0F0F0F] text-white rounded-full px-4 py-2 text-xs font-bold hover:bg-[#1c1c1c]" data-testid="mkt-save-btn">
              {savedFlag ? <BookmarkCheck size={13} /> : <Bookmark size={13} />} {savedFlag ? "Saved" : "Save Calculation"}
            </button>
          </div>
        </div>

        {/* TAM / SAM / SOM */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Funnel label="TAM" name="Total Market" value={calc.tamValue} sub={`${num(calc.totalMarket)} people`} tone="from-[#0F0F0F] to-[#23200f]" light testid="tam" />
          <Funnel label="SAM" name="Serviceable Market" value={calc.samValue} sub={`${num(calc.reachable)} reachable`} tone="from-[#8a6d1a] to-[#D4AF37]" light testid="sam" />
          <Funnel label="SOM" name="Obtainable Market" value={calc.somValue} sub={`${num(calc.payingCustomers)} paying`} tone="from-[#FBF7EA] to-white" testid="som" />
        </div>

        {/* Revenue metrics */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Stat label="Reachable Audience" value={num(calc.reachable)} testid="reachable" />
          <Stat label="Paying Customers" value={num(calc.payingCustomers)} testid="paying" />
          <Stat label="Monthly Revenue" value={money(calc.monthlyRevenue)} testid="monthly" />
          <Stat label="Annual Revenue" value={money(calc.annualRevenue)} accent testid="annual" />
        </div>

        {/* 5-year projection */}
        <div className="rounded-3xl border border-gray-100 bg-white p-6 shadow-[0_4px_20px_rgb(0,0,0,0.03)]" data-testid="mkt-projection">
          <h3 className="font-display text-lg font-bold text-[#0F0F0F] mb-1">Five-Year Revenue Projection</h3>
          <p className="text-xs text-[#687280] mb-4">Assuming {calc.growth}% annual growth.</p>
          <Projection data={calc.projection} />
        </div>

        {/* AI insights */}
        <div className="rounded-3xl border border-[#D4AF37]/30 bg-gradient-to-br from-[#FBF7EA]/60 to-white p-6" data-testid="mkt-ai-card">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h3 className="font-display text-lg font-bold text-[#0F0F0F] flex items-center gap-2"><Sparkles size={18} className="text-[#D4AF37]" /> AI Market Insights</h3>
              <p className="text-sm text-[#687280] mt-1">Blueprint AI estimated the numbers above and explains how it sized this opportunity.</p>
            </div>
            <button onClick={runInsights} disabled={loading} className="inline-flex items-center gap-2 border border-[#D4AF37] text-[#8a6d1a] rounded-full px-5 py-2.5 text-sm font-bold hover:bg-[#FBF7EA] transition-colors disabled:opacity-60 shrink-0" data-testid="mkt-ai-btn">
              {loading ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />} {insights ? "Re-estimate" : "Estimate Market"}
            </button>
          </div>

          {insights && (
            <div id="mkt-insights" className="mt-5 space-y-5" data-testid="mkt-insights">
              <div className="flex items-center gap-3">
                <span className="text-[11px] font-bold uppercase tracking-widest text-[#9aa1ab]">Verdict</span>
                <span className="text-sm font-extrabold px-3 py-1 rounded-full text-white" style={{ background: verdictColor(insights.verdict) }}>{insights.verdict} opportunity</span>
              </div>
              <p className="text-sm text-[#374151] leading-relaxed">{insights.summary}</p>

              {insights.reasoning && (
                <div className="rounded-2xl bg-white border border-gray-100 p-4" data-testid="mkt-reasoning">
                  <p className="text-[11px] font-bold uppercase tracking-widest text-[#8a6d1a] mb-3">How these numbers were estimated</p>
                  <div className="space-y-2.5">
                    {[
                      ["Total audience", insights.reasoning.total_market],
                      ["Market penetration", insights.reasoning.penetration],
                      ["Price per customer", insights.reasoning.price],
                      ["Conversion rate", insights.reasoning.conversion],
                      ["Annual growth", insights.reasoning.growth],
                    ].filter(([, v]) => v).map(([k, v]) => (
                      <div key={k} className="flex gap-3 text-sm">
                        <span className="w-36 shrink-0 font-semibold text-[#0F0F0F]">{k}</span>
                        <span className="text-[#687280]">{v}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="rounded-2xl bg-white border border-green-100 p-4">
                  <p className="font-bold text-sm text-green-700 mb-2 flex items-center gap-1.5"><Lightbulb size={15} /> Opportunities</p>
                  <ul className="space-y-1.5 text-sm text-[#374151]">{(insights.opportunities || []).map((x, i) => <li key={i} className="flex gap-1.5">•<span>{x}</span></li>)}</ul>
                </div>
                <div className="rounded-2xl bg-white border border-red-100 p-4">
                  <p className="font-bold text-sm text-red-600 mb-2 flex items-center gap-1.5"><AlertTriangle size={15} /> Risks</p>
                  <ul className="space-y-1.5 text-sm text-[#374151]">{(insights.risks || []).map((x, i) => <li key={i} className="flex gap-1.5">•<span>{x}</span></li>)}</ul>
                </div>
              </div>
              <div className="rounded-2xl bg-[#0F0F0F] text-white p-4">
                <p className="text-[11px] font-bold uppercase tracking-widest text-[#D4AF37] mb-1">Recommendation</p>
                <p className="text-sm leading-relaxed text-[#e8e6df]">{insights.recommendation}</p>
              </div>
            </div>
          )}
        </div>
      </section>
      </ResultPaywall>
    </AIResourcesShell>
  );
}

const Field = ({ icon: Icon, label, hint, value, min, max, step, onChange, prefix, suffix, testid, fmt }) => (
  <div>
    <div className="flex items-center gap-2">
      <Icon size={15} className="text-[#D4AF37]" />
      <label className="text-sm font-semibold text-[#0F0F0F]">{label}</label>
    </div>
    <p className="text-xs text-[#9aa1ab] mt-0.5">{hint}</p>
    <div className="flex items-center gap-3 mt-2.5">
      <input
        type="range" min={min} max={max} step={step} value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        className="flex-1 accent-[#D4AF37] cursor-pointer"
        data-testid={`mkt-slider-${testid}`}
      />
      <div className="flex items-center rounded-lg border border-gray-200 px-2.5 py-1.5 min-w-[110px] justify-end">
        {prefix && <span className="text-xs text-[#9aa1ab] mr-0.5">{prefix}</span>}
        <input
          type="number" value={value} min={min} max={max} step={step}
          onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
          className="w-full text-right text-sm font-bold text-[#0F0F0F] focus:outline-none bg-transparent"
          data-testid={`mkt-num-${testid}`}
        />
        {suffix && <span className="text-xs text-[#9aa1ab] ml-1">{suffix}</span>}
      </div>
    </div>
  </div>
);

const Funnel = ({ label, name, value, sub, tone, light, testid }) => (
  <div className={`rounded-2xl bg-gradient-to-br ${tone} p-5 ${light ? "text-white" : "text-[#0F0F0F] border border-[#D4AF37]/30"}`} data-testid={`mkt-${testid}`}>
    <p className={`text-[11px] font-bold uppercase tracking-widest ${light ? "text-white/70" : "text-[#8a6d1a]"}`}>{label}</p>
    <p className={`text-xs ${light ? "text-white/80" : "text-[#687280]"} mt-0.5`}>{name}</p>
    <p className="font-display text-2xl font-extrabold mt-2">{money(value)}</p>
    <p className={`text-xs mt-1 ${light ? "text-white/70" : "text-[#687280]"}`}>{sub}</p>
  </div>
);

const Stat = ({ label, value, accent, testid }) => (
  <div className={`rounded-2xl p-4 border ${accent ? "bg-[#FBF7EA] border-[#D4AF37]/40" : "bg-white border-gray-100"}`} data-testid={`mkt-stat-${testid}`}>
    <p className="text-[11px] uppercase font-bold text-[#9aa1ab]">{label}</p>
    <p className={`font-display text-xl font-extrabold mt-1 ${accent ? "text-[#8a6d1a]" : "text-[#0F0F0F]"}`}>{value}</p>
  </div>
);

const Projection = ({ data }) => {
  const W = 620, H = 220, padX = 40, padY = 24;
  const max = Math.max(...data.map((d) => d.revenue), 1);
  const stepX = (W - padX * 2) / (data.length - 1);
  const x = (i) => padX + i * stepX;
  const y = (v) => H - padY - (v / max) * (H - padY * 2);
  const line = data.map((d, i) => `${x(i)},${y(d.revenue)}`).join(" ");
  const area = `${padX},${H - padY} ${line} ${x(data.length - 1)},${H - padY}`;
  return (
    <div className="overflow-x-auto">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full min-w-[520px]" data-testid="mkt-chart">
        <defs>
          <linearGradient id="mktGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#D4AF37" stopOpacity="0.28" />
            <stop offset="100%" stopColor="#D4AF37" stopOpacity="0" />
          </linearGradient>
        </defs>
        {[0, 0.5, 1].map((g, i) => (
          <line key={i} x1={padX} x2={W - padX} y1={y(max * g)} y2={y(max * g)} stroke="#eee" strokeWidth="1" />
        ))}
        <polygon points={area} fill="url(#mktGrad)" />
        <polyline points={line} fill="none" stroke="#D4AF37" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
        {data.map((d, i) => (
          <g key={i}>
            <circle cx={x(i)} cy={y(d.revenue)} r="5" fill="#0F0F0F" stroke="#D4AF37" strokeWidth="2.5" />
            <text x={x(i)} y={y(d.revenue) - 12} textAnchor="middle" className="fill-[#0F0F0F]" style={{ fontSize: 12, fontWeight: 700 }}>{money(d.revenue)}</text>
            <text x={x(i)} y={H - 6} textAnchor="middle" className="fill-[#9aa1ab]" style={{ fontSize: 11 }}>{`Year ${d.year}`}</text>
          </g>
        ))}
      </svg>
    </div>
  );
};
